import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useQuery } from "@tanstack/react-query";
import { 
  addToCart as addToGuestCart, 
  removeFromCart as removeFromGuestCart, 
  clearCart as clearGuestCart, 
  updateCartItemQuantity as updateGuestCartQuantity,
  addToCartBulk as addToGuestCartBulk
} from "@/store/slices/appSlice";
import { 
  fetchAllCartItemsQueryOptions 
} from "@/queryOptions/cart/fetchAllCartItems";
import { 
  useCreateCartItem, 
  useUpdateCartItem, 
  useDeleteCartItem, 
  useBulkDeleteCartItems 
} from "@/queryOptions/cart/cartMutations";
import { Cart } from "@/types/AppTypes";
import { toast } from "sonner";
import { getUserFromLS } from "@/utils/helper/getUserFromLS";

export const useCartManagement = () => {
  const dispatch = useAppDispatch();
  const user = getUserFromLS();
  const { cartItems: guestCartItems } = useAppSelector((state) => state.app);

  // React Query mutations for authenticated users only
  const createCartItemMutation = useCreateCartItem();
  const updateCartItemMutation = useUpdateCartItem();
  const deleteCartItemMutation = useDeleteCartItem();
  const bulkDeleteCartItemsMutation = useBulkDeleteCartItems();

  // Fetch cart items for authenticated users only
  const shouldFetchCart = !!user?.user_id;
  
 
  
  const { 
    data: backendCartItems = [], 
    isLoading: backendLoading,
    refetch: refetchBackendCart,
    isError: backendError

  } = useQuery(
    fetchAllCartItemsQueryOptions(
      { user_id: user?.user_id ? { eq: user.user_id } : undefined },
      { 
        enabled: shouldFetchCart,
        staleTime: shouldFetchCart ? 1000 * 60 * 5 : Infinity, // 5 minutes if enabled, never stale if disabled
      }
    )
  );

  // Determine which cart to use
  const cartItems = useMemo(() => {
    if (user?.user_id) {
      return backendCartItems;
    }
    return guestCartItems;
  }, [user?.user_id, backendCartItems, guestCartItems]);

  const isLoading = useMemo(() => {
    if (user?.user_id) {
      return backendLoading;
    }
    return false;
  }, [user?.user_id, backendLoading]);


 

  // Add item to cart
  const addToCart = useCallback(async (product: any, quantity: number = 1, variant?: any) => {
    if (user?.user_id) {
      console.log("adding to cart normal ", product);
      // Add to backend for authenticated users (duplicate checking handled in mutation)
      await createCartItemMutation.mutateAsync({
        user_id: user.user_id,
        product_id: product.product_id,
        quantity,
        variant: JSON.stringify(product)
      });
    } else {
      // Check for duplicate in guest cart
      const existingItem = guestCartItems.find((item: any) => 
        item.product_id === product.id && 
        (!variant?.id || item.variant?.id === variant.id)
      );
      
      if (existingItem) {
        // Update existing item quantity
        dispatch(updateGuestCartQuantity({ 
          item: existingItem, 
          quantity: (existingItem.quantity || 1) + quantity 
        }));
        toast.success("Cart item quantity updated");
      } else {
        // Add new item to guest cart
        dispatch(addToGuestCart({
          id: `guest-${Date.now()}`,
          product_id: product.id,
          user_id: null,
          quantity,
          variant,
          ...product
        }));
        toast.success("Item added to cart");
      }
    }
  }, [user?.user_id, createCartItemMutation, dispatch, guestCartItems]);

  // Remove item from cart
  const removeFromCart = useCallback(async (itemId: string) => {
    if (user?.user_id) {
      // Remove from backend for authenticated users
      await deleteCartItemMutation.mutateAsync(itemId);
    } else {
      // Remove from guest cart for non-authenticated users
      dispatch(removeFromGuestCart(itemId));
      toast.success("Item removed from cart");
    }
  }, [user?.user_id, deleteCartItemMutation, dispatch]);

  // Update cart item quantity
  const updateCartItemQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (user?.user_id) {
      // Update in backend for authenticated users
      await updateCartItemMutation.mutateAsync({
        id: itemId,
        quantity
      });
    } else {
      // Update in guest cart for non-authenticated users
      const item = guestCartItems.find((cartItem: any) => cartItem.id === itemId);
      if (item) {
        dispatch(updateGuestCartQuantity({ item, quantity }));
        toast.success("Cart updated");
      }
    }
    }, [user?.user_id, updateCartItemMutation, dispatch, guestCartItems]);

  // Clear cart
  const clearCart = useCallback(async () => {
    if (user?.user_id) {
      // Clear from backend for authenticated users
      const itemIds = backendCartItems
        .map((item: any) => item.id)
        .filter((id): id is string => Boolean(id));
      if (itemIds.length > 0) {
        await bulkDeleteCartItemsMutation.mutateAsync(itemIds);
      }
    } else {
      // Clear guest cart for non-authenticated users
      dispatch(clearGuestCart());
    }
  }, [user?.user_id, backendCartItems, bulkDeleteCartItemsMutation, dispatch]);

  // Sync guest cart to backend when user logs in
  const syncGuestCartToBackend = useCallback(async () => {
    if (!user?.user_id || guestCartItems.length === 0) {
    
      return;
    }

  
    try {
      // Add all guest cart items to backend
      const syncPromises = guestCartItems.map((item: any) =>
        createCartItemMutation.mutateAsync({
          user_id: user.user_id!,
          product_id: item?.product_id,
          quantity: item.quantity,
          variant: JSON.stringify({
            id: item.id,
            name: item?.name,
            price: item?.price,
            images: item?.images,
            brand_id: item?.brand_id,
            category_id: item?.category_id,
            seller_id: item?.seller_id,
            attributes: item?.attributes,
            description: item?.description,
           is_active: item?.is_active,
           product_id: item?.product_id,
           show_by_default: item?.show_by_default,
           sku: item?.sku,
          })
        })
      );

      await Promise.all(syncPromises);
      
      // Clear guest cart after successful sync
      dispatch(clearGuestCart());

    } catch (error) {
      console.error("Error syncing cart:", error);
      toast.error("Failed to sync cart to account");
    }
  }, [user?.user_id, guestCartItems, createCartItemMutation, dispatch]);


  const cartTotal = useMemo(() => {
    return cartItems.reduce((total: number, item: any) => {
      const price = item.price || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  }, [cartItems]);

  const cartItemCount = useMemo(() => {
    return cartItems.length;
  }, [cartItems]);

  return {
    // Cart data
    cartItems,
    cartTotal,
    cartItemCount,
    isLoading,
    isError: backendError,
    
    // User state
    isAuthenticated: !!user?.user_id,
    isGuest: !user?.user_id,
    
    // Cart operations
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    syncGuestCartToBackend,
    refetchCart: refetchBackendCart,
    
    // Mutation states
    isAddingToCart: createCartItemMutation.isPending,
    isUpdatingCart: updateCartItemMutation.isPending,
    isRemovingFromCart: deleteCartItemMutation.isPending,
    isClearingCart: bulkDeleteCartItemsMutation.isPending,
  };
}; 